import { API_BASE, getAuthHeaders } from '@/lib/apiClient'

/**
 * Custom fetch mutator for Orval-generated clients.
 *
 * Orval invokes this with a path-only `url` (e.g. `/api/chat/`) and a standard
 * `RequestInit`. We:
 *   - prepend `API_BASE`
 *   - inject Firebase auth headers via `getAuthHeaders()` so generated clients
 *     reuse the same auth pipeline as `apiClient` in `src/lib/apiClient.ts`
 *   - default `Content-Type: application/json` when sending a string body so
 *     callers don't have to pass headers themselves
 *   - return `{ data, status, headers }` matching the generated client contract
 *
 * Mirrors the spirit of `apiClient.request()` but exposes the Orval-friendly
 * `(url, init) => Promise<T>` signature.
 */
export const customFetch = async <T>(url: string, init: RequestInit = {}): Promise<T> => {
  const authHeaders = await getAuthHeaders()

  const headers = new Headers(init.headers)

  // Default to JSON when the caller is sending a string body and hasn't set
  // Content-Type themselves. FormData / Blob / streams are left alone so the
  // browser can set the correct multipart boundary.
  if (typeof init.body === 'string' && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }

  // Auth headers are set last so callers can't accidentally clobber them.
  for (const [key, value] of Object.entries(authHeaders)) {
    headers.set(key, value)
  }

  const response = await fetch(`${API_BASE}${url}`, {
    ...init,
    headers,
  })

  const { status, headers: responseHeaders } = response

  let data: unknown
  if (status === 204) {
    data = null
  } else {
    const contentType = responseHeaders.get('Content-Type') ?? ''
    if (contentType.includes('text/event-stream')) {
      // Pass the raw Response through so callers can consume the SSE stream
      // via response.body without us eagerly buffering it.
      data = response
    } else if (contentType.includes('application/json')) {
      data = await response.json()
    } else {
      data = await response.text()
    }
  }

  return { data, status, headers: responseHeaders } as T
}

export default customFetch
